// 二次曲線の数学的計算ユーティリティ
export const conicSectionMath = {
  // 判別式による曲線分類
  classifyConicSection: (A, B, C) => {
    const discriminant = B * B - 4 * A * C;
    
    if (Math.abs(discriminant) < 1e-10) {
      return { type: "放物線", discriminant };
    } else if (discriminant < 0) {
      if (Math.abs(A - C) < 1e-10 && Math.abs(B) < 1e-10) {
        return { type: "円", discriminant };
      } else {
        return { type: "楕円", discriminant };
      }
    } else {
      return { type: "双曲線", discriminant };
    }
  },

  // 回転角の計算
  calculateRotationAngle: (A, B, C) => {
    if (Math.abs(B) < 1e-10) return 0;
    
    const angle = 0.5 * Math.atan2(B, A - C);
    return (angle * 180) / Math.PI;
  },

  // 標準形への変換
  convertToStandardForm: (coeffs, curveType) => {
    const { A, B, C, D, E, F } = coeffs;
    
    try {
      switch (curveType) {
        case "円":
          return convertCircleToStandardForm(A, B, C, D, E, F);
        case "楕円":
          return convertEllipseToStandardForm(A, B, C, D, E, F);
        case "放物線":
          return convertParabolaToStandardForm(A, B, C, D, E, F);
        case "双曲線":
          return convertHyperbolaToStandardForm(A, B, C, D, E, F);
        default:
          return "変換できません";
      }
    } catch (error) {
      return "変換エラー";
    }
  },

  // パラメータ計算
  calculateParameters: (coeffs, curveType) => {
    const { A, B, C, D, E, F } = coeffs;
    
    try {
      switch (curveType) {
        case "円":
          return calculateCircleParameters(A, B, C, D, E, F);
        case "楕円":
          return calculateEllipseParameters(A, B, C, D, E, F);
        case "放物線":
          return calculateParabolaParameters(A, B, C, D, E, F);
        case "双曲線":
          return calculateHyperbolaParameters(A, B, C, D, E, F);
        default:
          return {};
      }
    } catch (error) {
      return {};
    }
  },

  // グラフデータ生成
  generateGraphData: (coeffs, curveType, parameters) => {
    const { A, B, C, D, E, F } = coeffs;
    
    const colors = {
      "円": "#3b82f6",
      "楕円": "#10b981",
      "放物線": "#f59e0b",
      "双曲線": "#ef4444"
    };
    
    return {
      coefficients: coeffs,
      title: `${curveType}`,
      color: colors[curveType] || "#6b7280"
    };
  },

  // 完全な解析
  analyzeConicSection: (coeffs) => {
    const { A, B, C, D, E, F } = coeffs;
    
    // 係数の検証
    if (A === 0 && B === 0 && C === 0) {
      throw new Error("A, B, C がすべて0です");
    }
    
    // 曲線分類
    const classification = conicSectionMath.classifyConicSection(A, B, C);
    
    // 回転角
    const rotationAngle = conicSectionMath.calculateRotationAngle(A, B, C);
    
    // パラメータ計算
    const parameters = conicSectionMath.calculateParameters(coeffs, classification.type);
    
    // 標準形変換
    const standardForm = conicSectionMath.convertToStandardForm(coeffs, classification.type);
    
    // グラフデータ生成
    const graphData = conicSectionMath.generateGraphData(coeffs, classification.type, parameters);
    
    return {
      curveType: classification.type,
      discriminant: classification.discriminant,
      rotationAngle,
      parameters,
      standardForm,
      graphData,
      description: getDescription(classification.type)
    };
  }
};

// 補助関数群
function convertCircleToStandardForm(A, B, C, D, E, F) {
  if (Math.abs(B) > 1e-10) return "回転した円: 標準形変換には回転変換が必要";
  
  const h = -D / (2 * A);
  const k = -E / (2 * C);
  const r2 = h * h + k * k - F / A;
  
  if (r2 <= 0) return "虚円または点";
  
  const r = Math.sqrt(r2);
  return `(x - ${h.toFixed(2)})² + (y - ${k.toFixed(2)})² = ${r.toFixed(2)}²`;
}

function convertEllipseToStandardForm(A, B, C, D, E, F) {
  if (Math.abs(B) > 1e-10) return "回転した楕円: 標準形変換には回転変換が必要";
  
  const h = -D / (2 * A);
  const k = -E / (2 * C);
  const constant = A * h * h + C * k * k + F;
  
  if (constant >= 0) return "虚楕円";
  
  const a2 = -constant / A;
  const b2 = -constant / C;
  
  if (a2 <= 0 || b2 <= 0) return "退化した楕円";
  
  return `(x - ${h.toFixed(2)})²/${a2.toFixed(2)} + (y - ${k.toFixed(2)})²/${b2.toFixed(2)} = 1`;
}

function convertParabolaToStandardForm(A, B, C, D, E, F) {
  if (Math.abs(B) > 1e-10) return "回転した放物線: 標準形変換には回転変換が必要";
  
  if (Math.abs(A) < 1e-10 && Math.abs(C) > 1e-10) {
    // x² の項がない場合: x = ay² + by + c
    const a = 1 / C;
    const b = E / C;
    const c = F / C;
    return `x = ${a.toFixed(4)}y² + ${b.toFixed(4)}y + ${c.toFixed(4)}`;
  } else if (Math.abs(C) < 1e-10 && Math.abs(A) > 1e-10) {
    // y² の項がない場合: y = ax² + bx + c
    const a = 1 / A;
    const b = D / A;
    const c = F / A;
    return `y = ${a.toFixed(4)}x² + ${b.toFixed(4)}x + ${c.toFixed(4)}`;
  }
  
  return "標準形変換が複雑です";
}

function convertHyperbolaToStandardForm(A, B, C, D, E, F) {
  if (Math.abs(B) > 1e-10) return "回転した双曲線: 標準形変換には回転変換が必要";
  
  const h = -D / (2 * A);
  const k = -E / (2 * C);
  const constant = A * h * h + C * k * k + F;
  
  if (constant === 0) return "退化した双曲線";
  
  const a2 = -constant / A;
  const b2 = -constant / C;
  
  if (a2 > 0 && b2 < 0) {
    return `(x - ${h.toFixed(2)})²/${a2.toFixed(2)} - (y - ${k.toFixed(2)})²/${Math.abs(b2).toFixed(2)} = 1`;
  } else if (a2 < 0 && b2 > 0) {
    return `(y - ${k.toFixed(2)})²/${b2.toFixed(2)} - (x - ${h.toFixed(2)})²/${Math.abs(a2).toFixed(2)} = 1`;
  }
  
  return "標準形変換エラー";
}

function calculateCircleParameters(A, B, C, D, E, F) {
  if (Math.abs(B) > 1e-10) return { 注意: "回転した円のため、パラメータ計算が複雑です" };
  
  const h = -D / (2 * A);
  const k = -E / (2 * C);
  const r2 = h * h + k * k - F / A;
  
  if (r2 <= 0) return { エラー: "虚円または点" };
  
  return {
    中心: { x: h, y: k },
    半径: Math.sqrt(r2)
  };
}

function calculateEllipseParameters(A, B, C, D, E, F) {
  if (Math.abs(B) > 1e-10) return { 注意: "回転した楕円のため、パラメータ計算が複雑です" };
  
  const h = -D / (2 * A);
  const k = -E / (2 * C);
  const constant = A * h * h + C * k * k + F;
  
  if (constant >= 0) return { エラー: "虚楕円" };
  
  const a2 = -constant / A;
  const b2 = -constant / C;
  
  if (a2 <= 0 || b2 <= 0) return { エラー: "退化した楕円" };
  
  const a = Math.sqrt(a2);
  const b = Math.sqrt(b2);
  const c = Math.sqrt(Math.abs(a2 - b2));
  
  return {
    中心: { x: h, y: k },
    長半径: Math.max(a, b),
    短半径: Math.min(a, b),
    離心率: c / Math.max(a, b),
    焦点: a2 > b2 ? 
      [{ x: h + c, y: k }, { x: h - c, y: k }] :
      [{ x: h, y: k + c }, { x: h, y: k - c }]
  };
}

function calculateParabolaParameters(A, B, C, D, E, F) {
  if (Math.abs(B) > 1e-10) return { 注意: "回転した放物線のため、パラメータ計算が複雑です" };
  
  if (Math.abs(A) < 1e-10 && Math.abs(C) > 1e-10) {
    // x = ay² + by + c 形式
    const a = 1 / C;
    const b = E / C;
    const c = F / C;
    const vertex_y = -b / (2 * a);
    const vertex_x = a * vertex_y * vertex_y + b * vertex_y + c;
    const p = 1 / (4 * a);
    
    return {
      頂点: { x: vertex_x, y: vertex_y },
      焦点: { x: vertex_x + p, y: vertex_y },
      準線: `x = ${(vertex_x - p).toFixed(4)}`
    };
  } else if (Math.abs(C) < 1e-10 && Math.abs(A) > 1e-10) {
    // y = ax² + bx + c 形式
    const a = 1 / A;
    const b = D / A;
    const c = F / A;
    const vertex_x = -b / (2 * a);
    const vertex_y = a * vertex_x * vertex_x + b * vertex_x + c;
    const p = 1 / (4 * a);
    
    return {
      頂点: { x: vertex_x, y: vertex_y },
      焦点: { x: vertex_x, y: vertex_y + p },
      準線: `y = ${(vertex_y - p).toFixed(4)}`
    };
  }
  
  return { 注意: "パラメータ計算が複雑です" };
}

function calculateHyperbolaParameters(A, B, C, D, E, F) {
  if (Math.abs(B) > 1e-10) return { 注意: "回転した双曲線のため、パラメータ計算が複雑です" };
  
  const h = -D / (2 * A);
  const k = -E / (2 * C);
  const constant = A * h * h + C * k * k + F;
  
  if (constant === 0) return { エラー: "退化した双曲線" };
  
  const a2 = -constant / A;
  const b2 = -constant / C;
  
  if (a2 > 0 && b2 < 0) {
    const a = Math.sqrt(a2);
    const b = Math.sqrt(Math.abs(b2));
    const c = Math.sqrt(a2 + Math.abs(b2));
    
    return {
      中心: { x: h, y: k },
      実軸: 2 * a,
      虚軸: 2 * b,
      離心率: c / a,
      焦点: [{ x: h + c, y: k }, { x: h - c, y: k }]
    };
  } else if (a2 < 0 && b2 > 0) {
    const a = Math.sqrt(Math.abs(a2));
    const b = Math.sqrt(b2);
    const c = Math.sqrt(Math.abs(a2) + b2);
    
    return {
      中心: { x: h, y: k },
      実軸: 2 * b,
      虚軸: 2 * a,
      離心率: c / b,
      焦点: [{ x: h, y: k + c }, { x: h, y: k - c }]
    };
  }
  
  return { エラー: "パラメータ計算エラー" };
}

function generateMainCurveTrace(A, B, C, D, E, F, curveType) {
  const points = [];
  const resolution = 200;
  
  // 二次曲線の陰関数 F(x,y) = Ax² + Bxy + Cy² + Dx + Ey + F = 0
  // 等高線プロット用のグリッドデータ生成
  const xRange = Array.from({ length: resolution }, (_, i) => -10 + (20 * i) / (resolution - 1));
  const yRange = Array.from({ length: resolution }, (_, i) => -10 + (20 * i) / (resolution - 1));
  
  const zData = [];
  for (let j = 0; j < resolution; j++) {
    const row = [];
    for (let i = 0; i < resolution; i++) {
      const x = xRange[i];
      const y = yRange[j];
      const z = A * x * x + B * x * y + C * y * y + D * x + E * y + F;
      row.push(z);
    }
    zData.push(row);
  }
  
  const colors = {
    "円": "#3b82f6",
    "楕円": "#10b981",
    "放物線": "#f59e0b",
    "双曲線": "#ef4444"
  };
  
  return {
    type: 'contour',
    x: xRange,
    y: yRange,
    z: zData,
    contours: {
      start: 0,
      end: 0,
      size: 0,
      showlines: true,
      coloring: 'none'
    },
    line: {
      color: colors[curveType] || "#6b7280",
      width: 3
    },
    showscale: false,
    name: curveType,
    hovertemplate: `<b>${curveType}</b><br>x: %{x:.2f}<br>y: %{y:.2f}<extra></extra>`
  };
}

function generateFeaturePointsTrace(parameters, curveType) {
  const points = [];
  const labels = [];
  
  // 中心点
  if (parameters.中心) {
    points.push([parameters.中心.x, parameters.中心.y]);
    labels.push("中心");
  }
  
  // 頂点
  if (parameters.頂点) {
    points.push([parameters.頂点.x, parameters.頂点.y]);
    labels.push("頂点");
  }
  
  // 焦点
  if (parameters.焦点) {
    if (Array.isArray(parameters.焦点)) {
      parameters.焦点.forEach((focus, i) => {
        points.push([focus.x, focus.y]);
        labels.push(`焦点${i + 1}`);
      });
    } else {
      points.push([parameters.焦点.x, parameters.焦点.y]);
      labels.push("焦点");
    }
  }
  
  if (points.length === 0) return null;
  
  const x = points.map(p => p[0]);
  const y = points.map(p => p[1]);
  
  return {
    type: 'scatter',
    mode: 'markers+text',
    x: x,
    y: y,
    text: labels,
    textposition: 'top center',
    marker: {
      size: 8,
      color: '#dc2626',
      symbol: 'circle',
      line: {
        color: '#ffffff',
        width: 2
      }
    },
    name: '特徴点',
    hovertemplate: '<b>%{text}</b><br>(%{x:.2f}, %{y:.2f})<extra></extra>'
  };
}

function generateAuxiliaryLinesTrace(parameters, curveType) {
  // 準線や漸近線などの補助線を生成
  if (curveType === "放物線" && parameters.準線) {
    // 準線の描画は複雑なので、今回は省略
    return null;
  }
  
  if (curveType === "双曲線" && parameters.中心) {
    // 漸近線の描画は複雑なので、今回は省略
    return null;
  }
  
  return null;
}

function formatEquation(A, B, C, D, E, F) {
  const terms = [];
  
  if (A !== 0) {
    terms.push(A === 1 ? "x²" : A === -1 ? "-x²" : `${A}x²`);
  }
  if (B !== 0) {
    terms.push(B === 1 ? "xy" : B === -1 ? "-xy" : `${B}xy`);
  }
  if (C !== 0) {
    terms.push(C === 1 ? "y²" : C === -1 ? "-y²" : `${C}y²`);
  }
  if (D !== 0) {
    terms.push(D === 1 ? "x" : D === -1 ? "-x" : `${D}x`);
  }
  if (E !== 0) {
    terms.push(E === 1 ? "y" : E === -1 ? "-y" : `${E}y`);
  }
  if (F !== 0) {
    terms.push(F.toString());
  }
  
  return terms.join(" + ").replace(/\+ -/g, " - ") + " = 0";
}

function getDescription(curveType) {
  const descriptions = {
    "円": "すべての点が中心から等距離にある曲線",
    "楕円": "2つの焦点からの距離の和が一定な点の軌跡",
    "放物線": "焦点と準線からの距離が等しい点の軌跡",
    "双曲線": "2つの焦点からの距離の差が一定な点の軌跡"
  };
  return descriptions[curveType] || "二次曲線";
}